import type {
    Manifest as SDKManifest,
    ManifestV1 as SDKManifestV1,
    ManifestV2 as SDKManifestV2,
} from "@softr-io/blocks-sdk";

export type Manifest = SDKManifest;
export type ManifestV1 = SDKManifestV1;
export type ManifestV2 = SDKManifestV2;

declare global {
    interface Window {
        isViteBuildEnabled?: boolean;
    }
}

/**
 * @internal Exported for testing purposes only
 */
export const _cache: Map<`${string}/${string}`, Promise<Manifest>> = new Map();
export function fetchManifest(
    name: string,
    version: string,
    baseUrl: string,
    priority: RequestPriority = "auto",
): Promise<Manifest> {
    const cached = _cache.get(`${name}/${version}`);
    if (cached) {
        return cached;
    } else {
        const promise = _fetchManifest(name, version, baseUrl, priority);
        _cache.set(`${name}/${version}`, promise);
        return promise;
    }
}

async function _fetchManifest(
    name: string,
    version: string,
    baseUrl: string,
    priority: RequestPriority,
): Promise<Manifest> {
    const path = `${baseUrl}/${name}/${version}/manifest.json?crossorigin&v=0.0.1`;

    const headers: Record<string, string> = window.PLAYWRIGHT_TESTS
        ? {
              // This header is used by the test server to identify which app is making the request
              // so that it can suspend the request for testing loading states.
              "X-Softr-App-Id": window.__SOFTR_APP_ID__ ?? "",
          }
        : // AWS S3 rejects the requests with extra headers.
          // So we only add the "X-Softr-App-Id" header when running Playwright tests.
          {};
    try {
        return fetchJSON(path, headers, priority) as Promise<Manifest>;
    } catch (e) {
        if (process.env.NODE_ENV !== "test" && baseUrl.indexOf("localhost") !== -1) {
            console.error(`Failed to fetch manifest: ${name}/${version} from ${baseUrl}`, e);
            console.info("Trying to fetch manifest from prod");
            const fallbackUrl = `https://assets.softr-files.com/softr-blocks/prod/blocks/${name}/${version}/manifest.json`;
            return fetchJSON(fallbackUrl, headers, priority) as Promise<Manifest>;
        } else {
            throw e;
        }
    }
}

async function fetchJSON(url: string, headers: Record<string, string>, priority: RequestPriority): Promise<unknown> {
    const res = await fetch(url, { priority, headers });
    if (!res.ok) {
        throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
    }
    return res.json();
}
declare global {
    interface Window {
        PLAYWRIGHT_TESTS?: boolean;
        /**
         * Only used for playwright tests.
         * For other purposes, use `useAppConstants` hook
         */
        __SOFTR_APP_ID__?: string;
    }
}
